import { AuthProvider } from "@/hooks/useAuth";
import { TenantProvider, TenantSlugResolver } from "@/hooks/useTenant";
import { BRAND_NAME, PORTAL_TITLE_SUFFIX } from "@Simplicity-Education/core";
import * as Sentry from "@sentry/react";
import {
  DirtyFormProvider,
  lazyWithRetry,
  SonnerToaster as Sonner,
  ThemeProvider,
  Toaster,
  TooltipProvider,
} from "@simplicity/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense, type ReactNode } from "react";
import { BrowserRouter, Route, Routes } from "react-router";
import { DocumentTitleEffect, type RouteTitle } from "./components/DocumentTitleEffect";
import { ReleaseWatcher } from "./components/ReleaseWatcher";
import AddDashboard from "./pages/AddDashboard.tsx";
import AdminUsers from "./pages/AdminUsers.tsx";
import DashboardView from "./pages/DashboardView.tsx";
import Index from "./pages/Index.tsx";
import Login from "./pages/Login.tsx";
import NotFound from "./pages/NotFound.tsx";
import RootRedirect from "./pages/RootRedirect.tsx";
import Settings from "./pages/Settings.tsx";
import SuperAdminConsole from "./pages/SuperAdminConsole.tsx";
import TenantLayout from "./pages/TenantLayout.tsx";
import Unauthorized from "./pages/Unauthorized.tsx";

const SparkDashboardView = lazyWithRetry(() => import("./spark/SparkDashboardView"));

const TITLE_SUFFIX = PORTAL_TITLE_SUFFIX;
const TITLE_FALLBACK = BRAND_NAME;
const TENANT_PARENT_PATH = "/:tenantSlug";

/** Throws during render to exercise Sentry.ErrorBoundary + source-map symbolication. */
function SentryTest(): never {
  throw new Error("Sentry test from portal SentryTest component");
}

function SparkLoadingFallback() {
  // SIM-1811: use a <main> landmark (not a bare <div>) so the axe "region" rule
  // passes even when the a11y crawl captures this transient loading shell —
  // e.g. on /portal/login before the auth gate resolves. Mirrors Login.tsx.
  return (
    <main className="min-h-screen bg-background flex items-center justify-center">
      <p className="text-muted-foreground text-sm">Loading...</p>
    </main>
  );
}

/**
 * Single source of truth for every routable path in portal. Adding a route without a `title` is a
 * TypeScript compile error (WCAG 2.4.2 enforcement, SIM-1398). Path "" inside TENANT_CHILD_ROUTES
 * denotes the index route — it renders via `<Route index />` and matches the bare tenant URL
 * (`/:tenantSlug`).
 */
interface RouteSpec {
  path: string;
  title: string;
  render: () => ReactNode;
}

const GLOBAL_ROUTES: ReadonlyArray<RouteSpec> = [
  { path: "/login", title: "Sign in", render: () => <Login /> },
  { path: "/unauthorized", title: "Unauthorized", render: () => <Unauthorized /> },
  { path: "/super-admin", title: "Super admin", render: () => <SuperAdminConsole /> },
  { path: "/debug/sentry", title: "Sentry test", render: () => <SentryTest /> },
];

const TENANT_CHILD_ROUTES: ReadonlyArray<RouteSpec> = [
  { path: "", title: "Dashboards", render: () => <Index /> },
  { path: "dashboard/:slug", title: "Dashboard", render: () => <DashboardView /> },
  {
    path: "spark/:dashboardId/:pageSlug?",
    title: "Dashboard",
    render: () => (
      <Suspense fallback={<SparkLoadingFallback />}>
        <SparkDashboardView />
      </Suspense>
    ),
  },
  { path: "add-dashboard", title: "Add dashboard", render: () => <AddDashboard /> },
  { path: "settings", title: "Settings", render: () => <Settings /> },
  { path: "admin/users", title: "Users", render: () => <AdminUsers /> },
];

const TITLE_LOOKUP: ReadonlyArray<RouteTitle> = [
  ...GLOBAL_ROUTES.map((r) => ({ path: r.path, title: r.title })),
  // RootRedirect navigates immediately on mount — no title written for the
  // transient "/" frame; the redirected route's effect picks up the title.
  { path: "/", title: "" },
  ...TENANT_CHILD_ROUTES.map((r) => ({
    path: r.path === "" ? TENANT_PARENT_PATH : `${TENANT_PARENT_PATH}/${r.path}`,
    title: r.title,
  })),
  // Catch-all — anything not matched above is the NotFound page.
  { path: "*", title: "Not found" },
];

const queryClient = new QueryClient();

// Wrap react-router v7 Routes so Sentry instruments navigation transactions with
// route patterns (e.g. /:tenantSlug/dashboard/:slug) instead of raw URLs.
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light">
    <QueryClientProvider client={queryClient}>
      <DirtyFormProvider>
        <AuthProvider>
          <TenantProvider>
            {/* SIM-1617: explicit 700ms tooltip delay matches Radix default and
              makes the discoverability contract for `<TooltipButton>` callers
              visible at the provider site. */}
            <TooltipProvider delayDuration={700}>
              <Toaster />
              <Sonner />
              <ReleaseWatcher />
              <BrowserRouter
                basename={window.location.pathname.startsWith("/portal") ? "/portal" : "/"}
              >
                <DocumentTitleEffect
                  routeTitles={TITLE_LOOKUP}
                  suffix={TITLE_SUFFIX}
                  fallback={TITLE_FALLBACK}
                />
                <SentryRoutes>
                  {/* Global routes (no tenant context) */}
                  {GLOBAL_ROUTES.map((r) => (
                    <Route key={r.path} path={r.path} element={r.render()} />
                  ))}

                  {/* Root redirect: / → /:firstTenantSlug */}
                  <Route path="/" element={<RootRedirect />} />

                  {/* Tenant-scoped routes */}
                  <Route
                    path={TENANT_PARENT_PATH}
                    element={
                      <TenantSlugResolver>
                        <TenantLayout />
                      </TenantSlugResolver>
                    }
                  >
                    {TENANT_CHILD_ROUTES.map((r) =>
                      r.path === "" ? (
                        <Route key="__tenant_index" index element={r.render()} />
                      ) : (
                        <Route key={r.path} path={r.path} element={r.render()} />
                      ),
                    )}
                  </Route>

                  {/* Catch-all */}
                  <Route path="*" element={<NotFound />} />
                </SentryRoutes>
              </BrowserRouter>
            </TooltipProvider>
          </TenantProvider>
        </AuthProvider>
      </DirtyFormProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
